import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentOrganization } from '../../contexts/OrganizationContext';
import { CreateOrganizationModal } from '../organizations/CreateOrganizationModal';
import type { OrganizationListItem } from '../../types/organization';
import './OrgSwitcher.css';

const roleBadgeColors: Record<string, string> = {
  owner: '#7c3aed',
  admin: '#2563eb',
  member: '#059669',
  viewer: '#6b7280',
};

function OrgAvatar({
  org,
  size = 28,
}: {
  org: OrganizationListItem | { name: string; logo_url?: string };
  size?: number;
}) {
  const initial = org.name.charAt(0).toUpperCase();
  return (
    <div className="org-switcher__avatar" style={{ width: size, height: size }}>
      {org.logo_url ? <img src={org.logo_url} alt={org.name} /> : <span>{initial}</span>}
    </div>
  );
}

export function OrgSwitcher() {
  const navigate = useNavigate();
  const { currentOrg, currentOrgId, organizations, setCurrentOrgId, isOrgsLoading } =
    useCurrentOrganization();

  const [isOpen, setIsOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, close]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, close]);

  const handleSelect = (orgId: string) => {
    setCurrentOrgId(orgId);
    close();
    navigate(`/organizations/${orgId}`);
  };

  const handleCreateSuccess = (orgId: string) => {
    setShowCreateModal(false);
    setCurrentOrgId(orgId);
    navigate(`/organizations/${orgId}`);
  };

  const displayOrg = currentOrg ?? organizations.find(o => o.id === currentOrgId) ?? null;

  return (
    <div className="org-switcher" ref={rootRef}>
      <button
        type="button"
        className={`org-switcher__trigger ${isOpen ? 'is-open' : ''}`}
        onClick={() => setIsOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={isOrgsLoading && !displayOrg}
      >
        {displayOrg ? (
          <>
            <OrgAvatar org={displayOrg} />
            <div className="org-switcher__label">
              <span className="org-switcher__name">{displayOrg.name}</span>
              <span className="org-switcher__slug">/{displayOrg.slug}</span>
            </div>
          </>
        ) : (
          <>
            <div className="org-switcher__avatar org-switcher__avatar--placeholder" />
            <div className="org-switcher__label">
              <span className="org-switcher__name org-switcher__name--muted">
                {isOrgsLoading ? 'Loading…' : 'No organization'}
              </span>
            </div>
          </>
        )}
        <svg
          className="org-switcher__chevron"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 8 10 12 14 8" />
        </svg>
      </button>

      {isOpen && (
        <div className="org-switcher__menu" role="listbox">
          <div className="org-switcher__menu-header">Your organizations</div>

          <div className="org-switcher__list">
            {organizations.length === 0 && !isOrgsLoading && (
              <div className="org-switcher__empty">No organizations yet</div>
            )}

            {organizations.map(org => {
              const isActive = org.id === currentOrgId;
              return (
                <button
                  key={org.id}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`org-switcher__option ${isActive ? 'is-active' : ''}`}
                  onClick={() => handleSelect(org.id)}
                >
                  <OrgAvatar org={org} size={24} />
                  <div className="org-switcher__option-text">
                    <span className="org-switcher__option-name">{org.name}</span>
                    <span
                      className="org-switcher__option-role"
                      style={{ color: roleBadgeColors[org.role] }}
                    >
                      {org.role}
                    </span>
                  </div>
                  {isActive && (
                    <svg
                      className="org-switcher__check"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="4 10 8 14 16 6" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>

          <div className="org-switcher__divider" />

          <button
            type="button"
            className="org-switcher__action"
            onClick={() => {
              close();
              setShowCreateModal(true);
            }}
          >
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="10" y1="4" x2="10" y2="16" />
              <line x1="4" y1="10" x2="16" y2="10" />
            </svg>
            Create organization
          </button>

          <button
            type="button"
            className="org-switcher__action"
            onClick={() => {
              close();
              navigate('/organizations');
            }}
          >
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="14" height="3" rx="1" />
              <rect x="3" y="9" width="14" height="3" rx="1" />
              <rect x="3" y="14" width="14" height="3" rx="1" />
            </svg>
            View all organizations
          </button>
        </div>
      )}

      {showCreateModal && (
        <CreateOrganizationModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
}
